import moment from 'moment-timezone';

// Every saved consent stores the exact text that was shown, so a published
// version is never edited: change the wording by adding a new version for that
// program. The text lives here rather than in the language files for the same
// reason. See Docs enrollment/enrollment_consent.md.
const ITEMS_V2 = [
  { key: 'participation', text: 'They agree to participate in {program} and to take readings as directed.' },
  { key: 'data_sharing', text: 'Readings and related information will be shared with their provider(s) and care team.' },
  { key: 'cost_sharing', text: 'Cost-sharing (copay or coinsurance) may apply.' },
  { key: 'cancellation', text: 'They can cancel {program} at any time and must return the device(s) when they cancel.' },
  { key: 'single_biller', text: 'Only one practitioner can bill for {program} in a 30-day period.' },
];

// UNREVIEWED PROOF-OF-CONCEPT WORDING: the ACCESS items below were drafted by the RxCap dev team
// (RXC-1535, 2026-10-28) from CMS's ACCESS Request for Applications v1.1, section "Beneficiary
// Alignment", and CMS's "ACCESS Model Beneficiary Notices: Reference Language", blocks 1 (before
// the eligibility check) and 2 (before alignment). They have NOT been reviewed by compliance or
// legal. Before real patients enroll in ACCESS, have them reviewed and publish the reviewed text
// as a new template version (never edit version 1: every saved consent keeps the text it showed).
// Not covered yet: the participant-specific cost-sharing amount, the optional claims-data and
// TEMPO device disclosures, and the consent to continue required before each Follow-On Period.
const ACCESS_ITEMS_V1 = [
  { key: 'participation', text: 'They agree to receive services in the {program} track and to align with this practice.' },
  { key: 'voluntary', text: 'Alignment is voluntary; their decision does not affect their Medicare benefits.' },
  { key: 'cms_data_sharing', text: 'These services are part of a CMS payment model test, and their health information may be shared with CMS, including for the model\'s evaluation.' },
  { key: 'control_group', text: 'CMS may randomly assign them to a comparison group, which would make them ineligible to enroll in ACCESS for 12 months without affecting their Medicare coverage.' },
  { key: 'cost_sharing', text: 'They were told the monthly cost-sharing that applies, or that the practice waives it.' },
  { key: 'disenrollment', text: 'They may end their alignment, or switch to another participating provider, at any time beginning 90 days after alignment.' },
  { key: 'one_provider_per_track', text: 'Only one participating provider can serve them per ACCESS track at a time; they may align in other tracks they qualify for.' },
];

// Keyed by the API program name, upper-cased (getConsentTemplate).
const CONSENT_TEMPLATES = {
  RPM: { id: 'rpm-consent', version: 2, versionLabel: 'v2 · Oct 2026', shortName: 'RPM', items: ITEMS_V2 },
  RTM: { id: 'rtm-consent', version: 2, versionLabel: 'v2 · Oct 2026', shortName: 'RTM', items: ITEMS_V2 },
  CCM: { id: 'ccm-consent', version: 2, versionLabel: 'v2 · Oct 2026', shortName: 'CCM', items: ITEMS_V2 },
  PCM: { id: 'pcm-consent', version: 2, versionLabel: 'v2 · Oct 2026', shortName: 'PCM', items: ITEMS_V2 },
  CTAM: { id: 'ctam-consent', version: 2, versionLabel: 'v2 · Oct 2026', shortName: 'CTAM', items: ITEMS_V2 },
  // ACCESS tracks: see the note on ACCESS_ITEMS_V1. The backend requires an "access-" template id.
  ECKM: { id: 'access-eckm-consent', version: 1, versionLabel: 'v1 · Oct 2026', shortName: 'ACCESS eCKM', items: ACCESS_ITEMS_V1 },
  CKM: { id: 'access-ckm-consent', version: 1, versionLabel: 'v1 · Oct 2026', shortName: 'ACCESS CKM', items: ACCESS_ITEMS_V1 },
  MSK: { id: 'access-msk-consent', version: 1, versionLabel: 'v1 · Oct 2026', shortName: 'ACCESS MSK', items: ACCESS_ITEMS_V1 },
  BH: { id: 'access-bh-consent', version: 1, versionLabel: 'v1 · Oct 2026', shortName: 'ACCESS BH', items: ACCESS_ITEMS_V1 },
};

export const CONSENT_STATUS = {
  notObtained: 'not_obtained',
  verbal: 'verbal',
  written: 'written',
  declined: 'declined',
};

export const CONSENT_GIVEN_BY = {
  patient: 'patient',
  caregiver: 'caregiver',
};

export const CONSENT_DATE_FORMAT = 'YYYY-MM-DD';

export const CONSENT_OBTAINED_BY_MAX_LENGTH = 200;

export const getConsentTemplate = program => {
  const template = CONSENT_TEMPLATES[program?.toUpperCase()];
  if (!template) {
    return null;
  }
  return {
    ...template,
    items: template.items.map(item => ({ ...item, text: item.text.replace(/\{program\}/g, template.shortName) })),
  };
};

// moment's default zone is the user's portal timezone (set by components/TZ), as for every other date in the portal.
export const consentToday = () => moment().format(CONSENT_DATE_FORMAT);

// Whether the enrollment's consent is already saved; the checklist never changes these switches.
export const hasRecordedConsent = enrollment => !!(enrollment?.verbalConsent || enrollment?.writtenConsent);

// Whether the patient's enrollment details show a consent row. ACCESS records the consent at
// registration, not from there, so its row shows only a recorded consent.
export const showsEnrollmentConsent = enrollment => !!enrollment
  && (!getConsentTemplate(enrollment.program)?.id.startsWith('access-') || hasRecordedConsent(enrollment));

// `defaults` fills fields the form prefills, such as the logged-in user as "obtained by".
export const getConsentDraft = (draft, defaults = {}) => ({
  status: CONSENT_STATUS.notObtained,
  obtainedOn: consentToday(),
  obtainedBy: '',
  givenBy: CONSENT_GIVEN_BY.patient,
  checked: {},
  ...defaults,
  ...draft,
});

export const consentDraftNeedsChecklist = draft => [CONSENT_STATUS.verbal, CONSENT_STATUS.written].includes(draft?.status);

export const countCheckedConsentItems = (draft, template) => (template?.items || []).filter(item => draft?.checked?.[item.key]).length;

export const isConsentChecklistComplete = (draft, template) =>
  !!template && countCheckedConsentItems(draft, template) === template.items.length;

export const CONSENT_DRAFT_PROBLEMS = {
  status: 'status',
  date: 'date',
  futureDate: 'futureDate',
  obtainedBy: 'obtainedBy',
  items: 'items',
};

export const getConsentDraftProblems = (draft, template) => {
  const current = getConsentDraft(draft);
  if (!consentDraftNeedsChecklist(current)) {
    return [CONSENT_DRAFT_PROBLEMS.status];
  }
  const problems = [];
  const obtainedOn = moment(current.obtainedOn, CONSENT_DATE_FORMAT, true);
  if (!obtainedOn.isValid()) {
    problems.push(CONSENT_DRAFT_PROBLEMS.date);
  } else if (obtainedOn.isAfter(moment(), 'day')) {
    problems.push(CONSENT_DRAFT_PROBLEMS.futureDate);
  }
  const obtainedBy = (current.obtainedBy || '').trim();
  if (!obtainedBy || obtainedBy.length > CONSENT_OBTAINED_BY_MAX_LENGTH) {
    problems.push(CONSENT_DRAFT_PROBLEMS.obtainedBy);
  }
  if (!isConsentChecklistComplete(current, template)) {
    problems.push(CONSENT_DRAFT_PROBLEMS.items);
  }
  return problems;
};

export const consentDraftToRecord = (draft, template) => {
  if (getConsentDraftProblems(draft, template).length > 0) {
    return undefined;
  }
  const current = getConsentDraft(draft);
  return {
    status: current.status,
    obtained_on: current.obtainedOn,
    obtained_by: current.obtainedBy.trim(),
    given_by: current.givenBy,
    template_id: template.id,
    template_version: template.version,
    items: template.items.map(item => ({ key: item.key, text: item.text, confirmed: !!current.checked[item.key] })),
  };
};
